import React, { useState, useEffect } from "react";
import { injectIntl } from "react-intl";
import { Container, LayerSelectorLabel } from "../styled";
import ListItem from "./ListItem";

const getLayerById = (layers, id) => layers.find((layer) => layer.id === id);

const constructInitialCheckedMap = (layers, defaultLayers) => {
  let checkedMap = {};
  const hasDefault = defaultLayers?.length > 0;

  for (const layer of layers) {
    checkedMap[layer.id] = false;
  }

  if (hasDefault) {
    defaultLayers.forEach((layerId) => (checkedMap[layerId] = true));
  }

  return checkedMap;
};

const constructInitialExpandedMap = (layers, checkedMap) => {
  let expandedMap = {};

  for (const layer of layers) {
    let checked = false;
    if (checkedMap[layer.id]) {
      checked = true;
      expandParentLayer(layer, layers, expandedMap);
    }

    expandedMap[layer.id] = checked;
  }

  return expandedMap;
};

const expandParentLayer = (layer, layers, expandedMap) => {
  const { parentLayerId } = layer;
  const parentLayer = getLayerById(layers, parentLayerId);
  expandedMap[parentLayerId] = true;

  if (parentLayerId >= 0) {
    expandParentLayer(parentLayer, layers, expandedMap);
  }
};

const checkSubLayers = (layerid, checked, newCheckedMap, layers) => {
  const layer = getLayerById(layers, layerid);
  const { subLayerIds } = layer;

  subLayerIds.forEach((subLayerId) => {
    newCheckedMap[subLayerId] = checked;
    const subLayer = getLayerById(layers, subLayerId);
    if (subLayer.subLayerIds) {
      checkSubLayers(subLayerId, checked, newCheckedMap, layers);
    }
  });
};

const uncheckParentLayer = (layer, layers, newCheckedMap) => {
  const { parentLayerId } = layer;
  const parentLayer = getLayerById(layers, parentLayerId);

  newCheckedMap[parentLayerId] = false;

  if (parentLayer.parentLayerId >= 0) {
    uncheckParentLayer(parentLayer, layers, newCheckedMap);
  }
};

const setChecked = (checked, checkedMap, layer, layers, setCheckedMap) => {
  let newCheckedMap = { ...checkedMap };
  newCheckedMap[layer.id] = checked;

  if (layer.subLayerIds) {
    checkSubLayers(layer.id, checked, newCheckedMap, layers);
  }

  if (!checked && layer.parentLayerId >= 0) {
    uncheckParentLayer(layer, layers, newCheckedMap);
  }

  setCheckedMap(newCheckedMap);
};

const LayerSelector = (props) => {
  const {
    layers,
    setSelectedLayers,
    save,
    defaultLayers,
    intl,
    label = "Choose layers",
    style,
  } = props;

  const [checkedMap, setCheckedMap] = useState(() =>
    constructInitialCheckedMap(layers, defaultLayers)
  );
  const [expandedMap, setExpandedMap] = useState(() =>
    constructInitialExpandedMap(layers, checkedMap)
  );

  useEffect(() => {
    let selectedLayers = [];

    for (const [key, value] of Object.entries(checkedMap)) {
      if (value) {
        selectedLayers.push(key);
      }
    }

    setSelectedLayers(selectedLayers);
    save(selectedLayers);
  }, [checkedMap]);

  const handleListItemCheck = (checked, layer) => {
    setChecked(checked, checkedMap, layer, layers, setCheckedMap);
  };

  const handleListItemExpand = (expanded, layer) => {
    const newExpandedMap = { ...expandedMap };
    newExpandedMap[layer.id] = expanded;
    setExpandedMap(newExpandedMap);
  };

  return (
    <Container style={style}>
      <LayerSelectorLabel>
        {label && intl.formatMessage({ id: label })}:
      </LayerSelectorLabel>
      {layers && (
        <ul>
          {/* Start with rendering only the layers that does not have a parent layer since ListItem is recursive.
              This filter clause may always result in an array of size 1. But because I don't know if all layer
              structures starts with a single parent this is the catch all solution. - OA 2023-04-25*/}
          {layers
            .filter((layer) => layer.parentLayerId === -1)
            .map((layer) => (
              <ListItem
                key={`layer_${layer.id}`}
                layer={layer}
                layers={layers}
                checked={
                  checkedMap[layer.id] == null ? true : checkedMap[layer.id]
                }
                expanded={expandedMap[layer.id]}
                checkedMap={checkedMap}
                expandedMap={expandedMap}
                onCheck={handleListItemCheck}
                onExpand={handleListItemExpand}
              />
            ))}
        </ul>
      )}
    </Container>
  );
};

export default injectIntl(LayerSelector);
